// components/groupAdmin/feed/create/ArticleCreateCard.jsx
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Image, Link as LinkIcon, FileText, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { adminFeedApi } from '../feedApi';
import { validatePollInput, buildPollPayload } from '../utils/poll';

export default function ArticleCreateCard({ groupId, onCreated }) {
  const { t } = useTranslation();

  const [creating, setCreating] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [files, setFiles] = useState([]);

  const [hasPoll, setHasPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);

  const reset = () => {
    setTitle('');
    setDescription('');
    setArticleUrl('');
    setImageFile(null);
    setFiles([]);
    setHasPoll(false);
    setPollQuestion('');
    setPollOptions(['', '']);
  };

  const updatePollOption = (idx, value) => {
    setPollOptions((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const addPollOption = () => setPollOptions((prev) => [...prev, '']);
  const removePollOption = (idx) => setPollOptions((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error(t('groupAdminTabs.feed.toastTitleRequired', { defaultValue: 'Title is required' }));
      return;
    }

    if (hasPoll) {
      const v = validatePollInput(pollQuestion, pollOptions, t);
      if (!v.ok) {
        toast.error(v.message || t('groupAdminTabs.feed.toastPollInvalid'));
        return;
      }
    }

    try {
      setCreating(true);

      const formData = new FormData();
      formData.append('title', title.trim());
      if (description.trim()) formData.append('description', description.trim());
      if (articleUrl.trim()) formData.append('article_url', articleUrl.trim());

      if (imageFile) formData.append('image', imageFile);

      if (hasPoll) {
        // keep backward compatibility with your current backend: poll_question + poll_options
        formData.append('poll_question', pollQuestion.trim());
        const cleaned = pollOptions.map((o) => String(o || '').trim()).filter(Boolean);
        formData.append('poll_options', JSON.stringify(cleaned));

        // also include normalized poll object for future backend (harmless if ignored)
        const poll = buildPollPayload(pollQuestion, pollOptions);
        formData.append('poll', JSON.stringify(poll));
      }

      if (files && files.length > 0) {
        Array.from(files).forEach((file) => formData.append('attachments', file));
      }

      await adminFeedApi.createArticle(groupId, formData);

      toast.success(t('groupAdminTabs.feed.toastCreateSuccess', { defaultValue: 'Posted to feed' }));
      reset();
      onCreated?.();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || t('groupAdminTabs.feed.toastCreateError', { defaultValue: 'Failed to create post' }));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('groupAdminTabs.feed.createTitle', { defaultValue: 'Create a post' })}</CardTitle>
        <CardDescription>{t('groupAdminTabs.feed.createDescription', { defaultValue: 'Share an update, article, files, or a poll.' })}</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="feed-title">{t('groupAdminTabs.feed.titleField', { defaultValue: 'Title' })}</Label>
            <Input id="feed-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Upcoming community meeting" required />
          </div>

          <div>
            <Label htmlFor="feed-description">{t('groupAdminTabs.feed.descriptionField', { defaultValue: 'Description' })}</Label>
            <Textarea id="feed-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Add more details…" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="feed-article-url">
              {t('groupAdminTabs.feed.articleUrl', { defaultValue: 'Article URL' })}{' '}
              <span className="text-xs text-slate-500">{t('groupAdminTabs.common.optional', { defaultValue: '(optional)' })}</span>

            </Label>
            <div className="flex gap-2 items-center">
              <LinkIcon size={16} className="text-slate-500" />
              <Input id="feed-article-url" value={articleUrl} onChange={(e) => setArticleUrl(e.target.value)} placeholder="https://example.com/article" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              {t('groupAdminTabs.feed.uploadImage', { defaultValue: 'Upload image' })}{' '}
              <span className="text-xs text-slate-500">{t('groupAdminTabs.common.optional', { defaultValue: '(optional)' })}</span>

            </Label>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" className="relative overflow-hidden" onClick={() => document.getElementById('feed-image-input')?.click()}>
                <Image size={16} className="mr-2" />
                {imageFile ? t('groupAdminTabs.feed.changeImage', { defaultValue: 'Change image' }) : t('groupAdminTabs.feed.uploadImage', { defaultValue: 'Upload image' })}
              </Button>
              {imageFile && <span className="text-sm text-slate-600 truncate max-w-[150px]">{imageFile.name}</span>}
            </div>
            <input id="feed-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
          </div>

          <div className="space-y-2">
            <Label>
              {t('groupAdminTabs.feed.uploadFiles', { defaultValue: 'Upload files' })}{' '}
              <span className="text-xs text-slate-500">{t('groupAdminTabs.common.optional', { defaultValue: '(optional)' })}</span>

            </Label>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" className="relative overflow-hidden" onClick={() => document.getElementById('feed-files-input')?.click()}>
                <FileText size={16} className="mr-2" />
                {files?.length ? t('groupAdminTabs.feed.changeFiles', { defaultValue: 'Change files' }) : t('groupAdminTabs.feed.uploadFiles', { defaultValue: 'Upload files' })}
              </Button>
              {files?.length > 0 && (
                <span className="text-sm text-slate-600">
                  {files.length} {t('groupAdminTabs.feed.filesSelected', { defaultValue: 'files selected' })}
                </span>
              )}
            </div>
            <input id="feed-files-input" type="file" multiple className="hidden" onChange={(e) => setFiles(e.target.files || [])} />
          </div>

          {/* Poll */}
          <div className="space-y-3 border rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">{t('groupAdminTabs.feed.addPoll', { defaultValue: 'Add poll' })}</Label>
                <p className="text-xs text-slate-500">{t('groupAdminTabs.feed.pollHelp', { defaultValue: 'Ask a question and let members vote.' })}</p>
              </div>
              <Switch checked={hasPoll} onCheckedChange={setHasPoll} />
            </div>

            {hasPoll && (
              <div className="space-y-3 mt-2">
                <div>
                  <Label htmlFor="poll-question">{t('groupAdminTabs.feed.pollQuestion', { defaultValue: 'Poll question' })}</Label>
                  <Input id="poll-question" value={pollQuestion} onChange={(e) => setPollQuestion(e.target.value)} placeholder="e.g. Which date works best?" />
                </div>

                <div className="space-y-2">
                  <Label>{t('groupAdminTabs.feed.pollOptions', { defaultValue: 'Options' })}</Label>

                  {pollOptions.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input value={opt} onChange={(e) => updatePollOption(idx, e.target.value)} placeholder={t('groupAdminTabs.feed.pollOptionPlaceholder', { num: idx + 1, defaultValue: `Option ${idx + 1}` })} />
                      {pollOptions.length > 2 && (
                        <Button type="button" size="icon" variant="ghost" onClick={() => removePollOption(idx)}>
                          <X size={16} />
                        </Button>
                      )}
                    </div>
                  ))}

                  {pollOptions.length < 10 && (
                    <Button type="button" variant="outline" size="sm" onClick={addPollOption} className="mt-1">
                      <Plus size={14} className="mr-1" />
                      {t('groupAdminTabs.feed.addOption', { defaultValue: 'Add option' })}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={reset} disabled={creating}>
              {t('groupAdminTabs.feed.reset', { defaultValue: 'Reset' })}
            </Button>
            <Button type="submit" className="flex-1" disabled={creating}>
              {creating ? t('groupAdminTabs.feed.creating', { defaultValue: 'Posting…' }) : t('groupAdminTabs.feed.publish', { defaultValue: 'Publish' })}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
